import { createContext, createElement, useContext, type ReactNode } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { PUBLIC_V2_CONFIG, createPublicV2Client, type PublicV2Client, type PublicV2Config, type PublicV2Purchase } from "./public-v2";
import { PUBLIC_V2_MAX_PAGES, publicV2PurchasesQueryOptions, publicV2QueryDefaults, publicV2QueryKey } from "./public-v2-query-model";
import { parseId, agentStage, type AgentStage } from "./model";

export type Agent = PublicV2Purchase;
export type PublicData<T> = Readonly<{ state: "unavailable" | "loading" | "failed" | "empty" | "not-found" | "data"; data?: T; error?: string }>;
const Context = createContext<{ config: PublicV2Config; client?: PublicV2Client } | null>(null);
const defaultClient = PUBLIC_V2_CONFIG.kind === "configured" ? createPublicV2Client(PUBLIC_V2_CONFIG) : undefined;

export function PublicV2Provider({ config, client, children }: { config: PublicV2Config; client?: PublicV2Client; children: ReactNode }) {
  const actualClient = client ?? (config.kind === "configured" ? createPublicV2Client(config) : undefined);
  return createElement(Context.Provider, { value: { config, client: actualClient } }, children);
}
function useRuntime() {
  const injected = useContext(Context);
  return injected ?? { config: PUBLIC_V2_CONFIG, client: defaultClient };
}
function status<T>(q: { data: T | undefined; isPending: boolean; isError: boolean; error: unknown }): PublicData<T> {
  if (q.isError) {
    const code = (q.error as { code?: string } | undefined)?.code;
    return { state: code === "not_found" || code === "system_prompt_not_finalized" ? "not-found" : "failed", error: code ?? "unavailable" };
  }
  if (q.isPending || q.data === undefined) return { state: "loading" };
  return { state: "data", data: q.data };
}
function unavailableOrDisabled<T>(config: PublicV2Config, client: PublicV2Client | undefined): PublicData<T> | null {
  if (config.kind !== "configured" || !client) return { state: "unavailable" };
  return null;
}

export function usePublicDeployment() {
  const { config, client } = useRuntime();
  const disabled = unavailableOrDisabled<unknown>(config, client);
  const q = useQuery({ queryKey: config.kind === "configured" ? publicV2QueryKey(config, "deployment") : ["registry-v2", "unavailable"],
    queryFn: ({ signal }) => client!.deployment(signal), enabled: !disabled, retry: publicV2QueryDefaults.retry });
  return disabled ?? status(q);
}

export function usePurchases(options: { limit?: number; creator?: string; enabled?: boolean } = {}) {
  const { config, client } = useRuntime();
  const disabled = unavailableOrDisabled<readonly PublicV2Purchase[]>(config, client);
  const q = useInfiniteQuery(publicV2PurchasesQueryOptions(config, client, { ...options, enabled: !disabled && (options.enabled ?? true) }));
  if (disabled) return { ...disabled, purchases: [] as readonly PublicV2Purchase[], hasMore: false, hasRetainedPageLimit: false, loadMore: async () => undefined };
  const pages = q.data?.pages ?? [];
  // Do not expose a previous successful page as current after a failed refresh.
  const flattened = pages.flatMap((page) => page.purchases);
  const ids = new Set<string>();
  const duplicateIds = flattened.some((purchase) => ids.has(purchase.agentId) || !ids.add(purchase.agentId));
  const purchases = q.isError || duplicateIds ? [] : flattened;
  const state = q.isError || duplicateIds ? { state: "failed" as const, error: duplicateIds ? "invalid_response" : (q.error as { code?: string } | undefined)?.code ?? "unavailable" }
    : q.isPending ? { state: "loading" as const }
      : purchases.length === 0 ? { state: "empty" as const }
        : { state: "data" as const, data: purchases };
  return { ...state, purchases, hasMore: q.hasNextPage, hasRetainedPageLimit: pages.length >= PUBLIC_V2_MAX_PAGES, isLoading: q.isPending, isError: q.isError || duplicateIds, loadMore: q.fetchNextPage, isFetchingMore: q.isFetchingNextPage };
}
function useSingle<T>(resource: string, part: string, load: (client: PublicV2Client, signal: AbortSignal) => Promise<T>, active = true) {
  const { config, client } = useRuntime();
  const disabled = unavailableOrDisabled<T>(config, client);
  const key = config.kind === "configured" ? publicV2QueryKey(config, resource, part) : ["registry-v2", "unavailable", resource, part];
  const q = useQuery({ queryKey: key, queryFn: ({ signal }) => load(client!, signal), enabled: !disabled && active, retry: publicV2QueryDefaults.retry });
  return disabled ?? status(q);
}
export function usePurchase(id: string | null) { return useSingle("purchase", id ?? "none", (c,s) => c.purchase(id!,s), id !== null); }
export function usePurchasePrompt(id: string | null) { return useSingle("prompt", id ?? "none", (c,s) => c.purchasePrompt(id!,s), id !== null); }
export function useSystemPrompt(active = true) { return useSingle("system-prompt", "current", (c,s) => c.systemPrompt(s), active); }
export function useAgentsCreatedBy(address: string | undefined) { return usePurchases({ creator: address?.toLowerCase(), enabled: address !== undefined }); }
export { agentStage, parseId, type AgentStage };
